import axios from 'axios'
import { objectToMap } from '~conciergeUiCommons/helpers/NewMap'
import { AppointmentDate } from '~concierge/types/AppointmentTypes'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { storeToRefs } from 'pinia'
import { Ref } from 'vue'

export const BASE_API_PATH = '/api/alfred'

const getAppointmentStore = () => useAppointmentStore()
const getIdentityStore = () => useIdentityStore()

export async function fetchAvailableVehicles(model?: string, trim?: string, year?: string) {
  try {
    const appointmentStore = getAppointmentStore()
    appointmentStore.loadingVehicles = true

    if (model || trim || year) {
      const query = objectToQuery({ model: model, trim: trim, year: year })

      const response = await axios.get(`${BASE_API_PATH}/vehicles?${query}`)
      appointmentStore.availableVehicles = response.data?.vehicles
      appointmentStore.vehicleAvailabilityMessage = response.data?.message
      appointmentStore.queriedVehicleSlugs = response.data?.queriedVehicleSlugs
      appointmentStore.hasMoreVehicles = response.data?.hasMoreVehicles
      appointmentStore.loadingVehicles = false
    } else {
      const response = await axios.get(`${BASE_API_PATH}/vehicles`)
      appointmentStore.availableVehicles = response.data?.vehicles
      appointmentStore.hasMoreVehicles = response.data?.hasMoreVehicles
      appointmentStore.vehicleAvailabilityMessage = ''
      appointmentStore.loadingVehicles = false
    }
  } catch (error) {
    getAppointmentStore().loadingVehicles = false
    console.error(error)
  }
}

export async function fetchAvailableDates(
  apiEndpoint: string,
  options: { year?: number; month?: number; vehicles?: number[] },
  loading: Ref<boolean>
) {
  try {
    loading.value = true
    const { year, month, vehicles } = options
    const currentDate = new Date()

    const targetYear = year !== undefined ? year : currentDate.getFullYear()
    const targetMonth = month !== undefined ? month : currentDate.getMonth() + 1
    const baseRoute = `${BASE_API_PATH}${apiEndpoint}?year=${targetYear}&month=${targetMonth}`
    const url = vehicles?.length ? `${baseRoute}&vehicles=${vehicles.join(',')}` : baseRoute
    const response = await axios.get(url)

    const appointmentStore = getAppointmentStore()
    appointmentStore.availableDates = objectToMap(response?.data?.availabilities)

    if (response?.data?.unavailabilities?.length) {
      appointmentStore.disabledDates = [
        ...new Set([...appointmentStore.disabledDates, ...response.data?.unavailabilities])
      ]
    }
    loading.value = false
  } catch (error) {
    loading.value = false
    console.error(error)
  }
}
export async function fetchAvailabilitiesByRangeDates(
  apiEndpoint: string,
  loading: Ref<boolean>,
  options: { vehicles?: number[] }
) {
  try {
    loading.value = true
    const conciergeEvent = getIdentityStore().event

    if (!conciergeEvent || !conciergeEvent?.fr?.startDate || !conciergeEvent?.fr?.endDate) {
      throw new Error('Event not found')
    }
    const start = conciergeEvent.fr.startDate
    const end = conciergeEvent.fr.endDate
    let url = `${BASE_API_PATH}${apiEndpoint}?start=${start}&end=${end}`
    if (options.vehicles?.length) {
      url += `&vehicles=${options.vehicles.join(',')}`
    }
    const response = await axios.get(url)

    const appointmentStore = getAppointmentStore()
    appointmentStore.availableDates = objectToMap(response?.data?.availabilities)

    if (response?.data?.unavailabilities?.length) {
      appointmentStore.disabledDates = [
        ...new Set([...appointmentStore.disabledDates, ...response.data?.unavailabilities])
      ]
    }
    loading.value = false
  } catch (error) {
    loading.value = false
    console.error(error)
  }
}
export async function fetchAvailableElementsForEvent(apiEndpoint: string) {
  try {
    const conciergeEvent = getIdentityStore().event

    if (!conciergeEvent || !conciergeEvent?.fr?.startDate || !conciergeEvent?.fr?.endDate) {
      throw new Error('Event not found')
    }
    const start = conciergeEvent?.fr?.startDate
    const end = conciergeEvent?.fr?.endDate

    const response = await axios.get(`${BASE_API_PATH}${apiEndpoint}?start=${start}&end=${end}`)

    return response?.data?.data
  } catch (error) {
    console.error(error)
  }
}

export async function fetchAvailableDatesByAdviser(
  adviserId: number,
  year?: number,
  month?: number
) {
  const { loadingDates } = storeToRefs(getAppointmentStore())
  await fetchAvailableDates(`/advisers/${adviserId}/availabilities`, { year, month }, loadingDates)
}

export async function fetchAvailableAdvisersByRangeDates(adviserId: number) {
  const { loadingDates } = storeToRefs(getAppointmentStore())

  await fetchAvailabilitiesByRangeDates(
    `/advisers/${adviserId}/availabilities-by-range`,
    loadingDates,
    {}
  )
}

export async function fetchAvailableDatesForAllAdvisers(year: number, month: number) {
  const { loadingDates } = storeToRefs(getAppointmentStore())
  await fetchAvailableDates('/availabilities', { year, month }, loadingDates)
}

export async function fetchAvailableDatesByVehicles(
  vehicleIds: number[],
  year?: number,
  month?: number
) {
  const { loadingDates } = storeToRefs(getAppointmentStore())

  await fetchAvailableDates(
    `/vehicles/availabilities`,
    { vehicles: vehicleIds, year, month },
    loadingDates
  )
}

export async function fetchAvailableDatesByVehiclesByRangeDates(vehicleIds: number[]) {
  const { loadingDates } = storeToRefs(getAppointmentStore())
  await fetchAvailabilitiesByRangeDates(`/vehicles/availabilities-by-range`, loadingDates, {
    vehicles: vehicleIds
  })
}

export async function fetchAvailableAdvisers() {
  const appointmentStore = getAppointmentStore()
  const { loadingAdvisers } = storeToRefs(appointmentStore)
  loadingAdvisers.value = true
  try {
    const response = await axios.get(`${BASE_API_PATH}/advisers`)
    appointmentStore.setAvailableAdvisers(response.data?.data || [])
    loadingAdvisers.value = false
  } catch (error) {
    loadingAdvisers.value = false
    console.error(error)
  }
}

export async function fetchAvailableAdvisersOnAGivenRange() {
  const appointmentStore = getAppointmentStore()
  const { loadingAdvisers } = storeToRefs(appointmentStore)
  loadingAdvisers.value = true
  try {
    const response = await fetchAvailableElementsForEvent(`/availabilities/specific-range-date`)
    appointmentStore.setAvailableAdvisers(response || [])
    loadingAdvisers.value = false
  } catch (error) {
    loadingAdvisers.value = false
    console.error(error)
  }
}
export async function fetchAvailableAdvisersForSpecificHour(date: string | null, time?: string) {
  const appointmentStore = getAppointmentStore()
  const { loadingAdvisers } = storeToRefs(appointmentStore)
  loadingAdvisers.value = true
  try {
    const response = await axios.get(
      `${BASE_API_PATH}/availabilities/specific-time?date=${date}&time=${time}`
    )
    appointmentStore.setAvailableAdvisers(response.data?.data || [])
    loadingAdvisers.value = false
  } catch (error) {
    loadingAdvisers.value = false
    console.error(error)
  }
}
export async function fetchAvailableAdvisersForSpecificHourForTestDrive(
  date: string | null,
  time?: string
) {
  const appointmentStore = getAppointmentStore()
  const { loadingAdvisers } = storeToRefs(appointmentStore)
  loadingAdvisers.value = true
  try {
    const response = await axios.get(
      `${BASE_API_PATH}/availabilities/specific-time?date=${date}&time=${time}&test_drive=true`
    )
    appointmentStore.setAvailableAdvisers(response.data?.data || [])
    loadingAdvisers.value = false
  } catch (error) {
    loadingAdvisers.value = false
    console.error(error)
  }
}

export function setAvailableHoursByDate(date: AppointmentDate) {
  const appointmentStore = getAppointmentStore()
  const { loadingHours } = storeToRefs(appointmentStore)
  loadingHours.value = true
  if (appointmentStore.availableDates?.has(date)) {
    appointmentStore.selectedDateHours = appointmentStore.availableDates.get(date) ?? []
    loadingHours.value = false
  } else {
    loadingHours.value = false
    console.error(date + ' not found')
  }
}

function objectToQuery(obj: Record<string, string | null | undefined>): string {
  if (Object.keys(obj).length === 0) {
    return ''
  }
  const queryArray: string[] = []

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key]
      if (typeof value === 'string' && value.length > 0) {
        queryArray.push(encodeURIComponent(key) + '=' + encodeURIComponent(value))
      }
    }
  }

  return queryArray.join('&')
}
